/**
 * Read and rewrite single filters inside an issue search string.
 *
 * The search text stays the one source of truth, so quick filters and typing
 * `env:staging` by hand can never disagree, and the URL remains shareable.
 * Tokenizing mirrors `outpost_core::search`.
 */

export interface QueryFilter {
  /** Accepted keys, lowercase. The first is the one written back. */
  keys: readonly string[];
}

export const ENVIRONMENT: QueryFilter = { keys: ['environment', 'env'] };
export const RELEASE: QueryFilter = { keys: ['release'] };
export const PLATFORM: QueryFilter = { keys: ['platform'] };
/**
 * `assigned:` takes `me`, `none` or an email address. The server keeps only the
 * last one it parses, so unlike the others this filter holds a single value.
 */
export const ASSIGNEE: QueryFilter = { keys: ['assigned'] };

/** Values of `filter` in `query`, in the order they appear. */
export function filterValues(query: string, filter: QueryFilter): string[] {
  return terms(query).flatMap((term) => {
    const value = valueOf(term, filter);
    return value === null ? [] : [value];
  });
}

/**
 * Replace every occurrence of `filter` in `query` with `values`, leaving all
 * other terms exactly as they were typed.
 */
export function withFilterValues(query: string, filter: QueryFilter, values: string[]): string {
  const kept = terms(query).filter((term) => valueOf(term, filter) === null);
  const added = [...new Set(values)].map((value) => `${filter.keys[0]}:${quote(value)}`);
  return [...kept, ...added].join(' ');
}

/** Split on whitespace outside quotes, keeping the quotes in each term. */
function terms(query: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (const char of query) {
    if (char === '"') inQuotes = !inQuotes;
    if (/\s/.test(char) && !inQuotes) {
      if (current) result.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  if (current) result.push(current);
  return result;
}

function valueOf(term: string, filter: QueryFilter): string | null {
  // The server drops quotes wherever they appear, so `env:"a b"` and
  // `"env:a b"` are the same term.
  const unquoted = term.replaceAll('"', '');
  const colon = unquoted.indexOf(':');
  if (colon <= 0) return null;
  const key = unquoted.slice(0, colon).toLowerCase();
  const value = unquoted.slice(colon + 1);
  return filter.keys.includes(key) && value ? value : null;
}

function quote(value: string): string {
  // The grammar has no escape for a literal quote, so one cannot survive.
  const bare = value.replaceAll('"', '');
  return /\s/.test(bare) ? `"${bare}"` : bare;
}
